export interface Poller {
  start: () => void;
  stop: () => void;
  setInterval: (newInterval: number) => void;
}

export interface PollerOptions {
  /** Run the first poll right away on start (default) or only after the first interval. */
  immediate?: boolean;
  onError?: (err: unknown) => void;
}

/** Repeatedly awaits `fetchFn` and hands the result to `onData`, waiting `interval` ms between completions. */
export function createPoller<T>(
  fetchFn: () => Promise<T>,
  onData: (data: T) => void,
  interval = 2000,
  { immediate = true, onError = err => console.error('Polling error:', err) }: PollerOptions = {}
): Poller {
  let timerId: ReturnType<typeof setTimeout> | null = null;
  let running = false;

  const schedule = () => {
    if (running) {
      timerId = setTimeout(poll, interval);
    }
  };

  const poll = async () => {
    if (!running) return;
    try {
      const data = await fetchFn();
      if (running) onData(data);
    } catch (err) {
      onError(err);
    }
    schedule();
  };

  return {
    start() {
      if (running) return;
      running = true;
      if (immediate) {
        poll();
      } else {
        schedule();
      }
    },
    stop() {
      running = false;
      if (timerId) {
        clearTimeout(timerId);
        timerId = null;
      }
    },
    setInterval(newInterval) {
      interval = newInterval;
    },
  };
}
